import { Component, type ErrorInfo, type ReactNode } from 'react'
import { captureError } from '@/lib/sentry'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  /** Pass a key that changes on navigation (e.g. location.pathname) to auto-reset on route changes */
  resetKey?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
    captureError(error, { componentStack: errorInfo.componentStack })
  }

  componentDidUpdate(prevProps: Props) {
    // Auto-reset error state when the route (resetKey) changes
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null })
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  private isChunkError(): boolean {
    const msg = (this.state.error?.message || '').toLowerCase()
    return (
      msg.includes('dynamically imported module') ||
      msg.includes('failed to fetch') ||
      msg.includes('loading chunk') ||
      msg.includes('load failed') ||
      msg.includes('importing a module script failed') ||
      this.state.error?.name === 'ChunkLoadError'
    )
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      const isChunk = this.isChunkError()

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8 text-center">
          <div className="rounded-full bg-red-50 p-3">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">
            {isChunk ? 'Page failed to load' : 'Something went wrong'}
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {isChunk
              ? 'A newer version of the app is available. Refreshing will load the latest version.'
              : 'An unexpected error occurred. You can try again or refresh the page.'}
          </p>
          <div className="flex gap-3">
            {!isChunk && (
              <button
                onClick={this.handleRetry}
                className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
              >
                Try again
              </button>
            )}
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-certify-600 px-4 py-2 text-sm font-medium text-white hover:bg-certify-700 transition-colors"
            >
              {isChunk ? 'Update now' : 'Refresh page'}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
